import type { SearchResponse, EntityType } from '@/api/search';
import { ALL_ENTITY_TYPES } from '@/api/search';
import { ResultGroup } from './ResultGroup';

export interface ResultsPanelProps {
  query: string;
  data?: SearchResponse;
  isLoading: boolean;
  isError: boolean;
}

export function ResultsPanel({ query, data, isLoading, isError }: ResultsPanelProps) {
  if (isLoading) {
    return (
      <div className="mt-2 space-y-2 rounded-md border border-slate-200 bg-white p-4 shadow-sm">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-slate-100" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        Search failed. Try again in a moment.
      </div>
    );
  }
  if (!data) return null;

  const totalMatches = Object.values(data.groups).reduce((acc, g) => acc + (g?.total ?? 0), 0);
  const showCustomer = data.scoped_to_customer_id === null;

  if (totalMatches === 0) {
    return (
      <div className="mt-2 rounded-md border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500 shadow-sm">
        No matches for <span className="font-medium">&ldquo;{query}&rdquo;</span>. Try a different spelling or remove a filter.
      </div>
    );
  }

  return (
    <div className="mt-2 max-h-[70vh] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
      {ALL_ENTITY_TYPES.map((t: EntityType) => {
        const g = data.groups[t];
        if (!g || g.total === 0) return null;
        return <ResultGroup key={t} type={t} group={g} query={query} showCustomer={showCustomer} />;
      })}
    </div>
  );
}
